import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";

const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "RAPID NFR", href: "#rapid" },
  { label: "Locations", href: "#locations" },
  { label: "Reviews", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

const BOOK_URL = "https://kneadrelief.noterro.com/";
const LOGO_SRC =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_knead-refresh/artifacts/tftgtah7_KneadRelief_logo_transparent_highres.webp";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "backdrop-blur-xl bg-white/95 border-b border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
          : "bg-white/40 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* ============ DESKTOP — two rows: nav links on top, big logo below ============ */}
        <div className="hidden lg:block">
          {/* Top row: nav links + phone + Book Now */}
          <div className="flex items-center justify-between h-[56px] border-b border-slate-200/0">
            <a
              href="tel:+14037147058"
              data-testid="nav-call"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-blue-800 transition-colors"
            >
              <Phone size={15} strokeWidth={2} />
              403-714-7058
            </a>

            <nav className="flex items-center gap-0.5">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="px-3.5 py-2 text-sm font-medium text-slate-700 hover:text-blue-800 transition-colors rounded-full hover:bg-blue-50/60"
                >
                  {l.label}
                </a>
              ))}
            </nav>

            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-now"
              className="inline-flex items-center bg-blue-800 text-white hover:bg-blue-900 transition-all hover:-translate-y-0.5 rounded-full px-5 py-2 text-sm font-semibold shadow-lg shadow-blue-900/20"
            >
              Book Now
            </a>
          </div>

          {/* Bottom row: big centered logo + brand text */}
          <a
            href="#home"
            data-testid="nav-logo"
            className="flex items-center justify-center py-3 group"
          >
            <img
              src={LOGO_SRC}
              alt="KneadRelief — Pain Relief · Movement · Recovery"
              className="object-contain h-40"
              style={{ maxWidth: "720px" }}
            />
          </a>
        </div>

        {/* ============ MOBILE — compact single row ============ */}
        <div className="lg:hidden flex items-center justify-between h-[72px]">
          <a
            href="#home"
            data-testid="nav-logo-mobile"
            className="flex items-center gap-2.5 group"
          >
            <img
              src={LOGO_SRC}
              alt="KneadRelief logo"
              className="h-10 w-auto object-contain"
            />
          </a>

          <div className="flex items-center gap-2">
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-now-mobile"
              className="hidden sm:inline-flex items-center bg-blue-800 text-white hover:bg-blue-900 rounded-full px-4 py-2 text-sm font-semibold"
            >
              Book Now
            </a>
            <button
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* ============ MOBILE DRAWER ============ */}
        {open && (
          <div
            className="lg:hidden pb-6 pt-2 border-t border-slate-200/60"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 mt-3">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 text-base font-medium text-slate-800 hover:bg-blue-50 rounded-xl"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="tel:+14037147058"
                className="px-4 py-3 text-base font-medium text-slate-600 hover:bg-blue-50 rounded-xl inline-flex items-center gap-2"
              >
                <Phone size={16} strokeWidth={2} />
                403-714-7058
              </a>
              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center justify-center bg-blue-800 text-white rounded-full px-5 py-3 text-sm font-semibold"
              >
                Book Your Appointment
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
